import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { 
  ArrowLeft, Youtube, Plus, Clock, CheckCircle, XCircle, 
  AlertCircle, Trash2, ExternalLink, Eye
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function YouTubeVideos() {
  const { user, getAuthHeader, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [myVideos, setMyVideos] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    youtube_url: '',
    video_title: '',
    description: '',
    thumbnail_url: '',
    display_on_profile: true
  });

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login');
      return;
    }
    if (user) {
      fetchMyVideos();
    }
  }, [user, authLoading]);

  const fetchMyVideos = async () => {
    try {
      const headers = getAuthHeader();
      const response = await axios.get(`${API_URL}/api/youtube-videos/my-videos`, { headers });
      setMyVideos(response.data);
    } catch (error) {
      console.error('Failed to fetch videos:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.youtube_url || !formData.video_title) {
      toast.error('YouTube URL and title are required');
      return;
    }

    setSubmitting(true);
    try {
      const headers = getAuthHeader();
      await axios.post(`${API_URL}/api/youtube-videos/submit`, formData, { headers });
      toast.success('Video submitted for approval!');
      setFormData({
        youtube_url: '',
        video_title: '',
        description: '',
        thumbnail_url: '',
        display_on_profile: true
      });
      setShowForm(false);
      fetchMyVideos();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit video');
    } finally {
      setSubmitting(false);
    }
  };

  const deleteVideo = async (videoId) => {
    if (!confirm('Are you sure you want to delete this video?')) return;
    
    try {
      const headers = getAuthHeader();
      await axios.delete(`${API_URL}/api/youtube-videos/${videoId}`, { headers });
      toast.success('Video deleted');
      fetchMyVideos();
    } catch (error) {
      toast.error('Failed to delete video');
    }
  };

  const extractVideoId = (url) => {
    const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&?/]+)/);
    return match ? match[1] : null;
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-green-500/20 text-green-400">
            <CheckCircle className="w-3 h-3" /> Approved
          </span>
        );
      case 'rejected':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-red-500/20 text-red-400">
            <XCircle className="w-3 h-3" /> Rejected
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-yellow-500/20 text-yellow-400">
            <Clock className="w-3 h-3" /> Pending Review
          </span>
        );
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="wm-glass sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-4">
              <Link to="/dashboard">
                <Button variant="ghost" size="sm" className="text-foreground hover:text-primary">
                  <ArrowLeft className="w-4 h-4 mr-2" /> Dashboard
                </Button>
              </Link>
              <h1 className="text-xl font-playfair font-bold text-foreground flex items-center gap-2">
                <Youtube className="w-6 h-6 text-red-500" /> My YouTube Videos
              </h1>
            </div>
            <Button
              onClick={() => setShowForm(!showForm)}
              className="bg-red-600 text-white hover:bg-red-700"
              data-testid="add-video-btn"
            >
              <Plus className="w-4 h-4 mr-2" /> Submit Video
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Info Banner */}
        <div className="wm-card p-4 rounded-xl mb-6 flex items-start gap-3 border border-primary/20">
          <AlertCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-foreground font-medium">Video Approval Process</p>
            <p className="text-sm text-muted-foreground">
              All YouTube videos must be approved by an admin before appearing on your profile or the WeMasterminds landing page. 
              This ensures all content aligns with our community standards.
            </p>
          </div>
        </div>

        {/* Submit Form */}
        {showForm && (
          <div className="wm-card p-6 rounded-xl mb-8" data-testid="video-submit-form">
            <h2 className="text-lg font-semibold text-foreground mb-4">Submit a YouTube Video</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="youtube_url" className="text-foreground">YouTube URL *</Label>
                <Input
                  id="youtube_url"
                  value={formData.youtube_url}
                  onChange={(e) => setFormData({ ...formData, youtube_url: e.target.value })}
                  placeholder="https://youtube.com/watch?v=..."
                  className="mt-1 bg-input border-border text-foreground"
                  data-testid="youtube-url-input"
                />
                {formData.youtube_url && extractVideoId(formData.youtube_url) && (
                  <div className="mt-3 rounded-lg overflow-hidden aspect-video bg-slate-900">
                    <iframe
                      src={`https://www.youtube.com/embed/${extractVideoId(formData.youtube_url)}`}
                      className="w-full h-full"
                      allowFullScreen
                      title="Video Preview"
                    />
                  </div>
                )}
              </div>

              <div>
                <Label htmlFor="video_title" className="text-foreground">Video Title *</Label>
                <Input
                  id="video_title"
                  value={formData.video_title}
                  onChange={(e) => setFormData({ ...formData, video_title: e.target.value })}
                  placeholder="My Business Success Story"
                  className="mt-1 bg-input border-border text-foreground"
                  data-testid="video-title-input"
                />
              </div>

              <div>
                <Label htmlFor="description" className="text-foreground">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Tell us about this video..."
                  rows={3}
                  className="mt-1 bg-input border-border text-foreground"
                />
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="display_on_profile"
                  checked={formData.display_on_profile}
                  onChange={(e) => setFormData({ ...formData, display_on_profile: e.target.checked })}
                  className="rounded border-border"
                />
                <Label htmlFor="display_on_profile" className="text-sm text-muted-foreground cursor-pointer">
                  Display on my public profile (once approved)
                </Label>
              </div>

              <div className="flex gap-3">
                <Button
                  type="submit"
                  disabled={submitting}
                  className="bg-red-600 text-white hover:bg-red-700"
                  data-testid="submit-video-btn"
                >
                  {submitting ? 'Submitting...' : 'Submit for Approval'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowForm(false)}
                  className="border-border text-foreground"
                >
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        )}

        {/* My Videos List */}
        <div className="space-y-4">
          <h2 className="text-xl font-semibold text-foreground">
            {myVideos.length > 0 ? `Your Videos (${myVideos.length})` : 'No Videos Submitted Yet'}
          </h2>

          {myVideos.length === 0 ? (
            <div className="text-center py-16 wm-card rounded-xl">
              <Youtube className="w-16 h-16 text-red-500/30 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-foreground mb-2">Share Your Content</h3>
              <p className="text-muted-foreground mb-4">
                Submit your YouTube videos to be featured on WeMasterminds
              </p>
              <Button onClick={() => setShowForm(true)} className="bg-red-600 text-white hover:bg-red-700">
                <Plus className="w-4 h-4 mr-2" /> Submit Your First Video
              </Button>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-4">
              {myVideos.map(video => {
                const videoId = extractVideoId(video.youtube_url);
                return (
                  <div key={video.id} className="wm-card rounded-xl overflow-hidden" data-testid="video-card">
                    <div className="aspect-video bg-slate-900 relative">
                      {videoId ? (
                        <iframe
                          src={`https://www.youtube.com/embed/${videoId}`}
                          title={video.video_title}
                          className="w-full h-full"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Youtube className="w-12 h-12 text-red-500/30" />
                        </div>
                      )}
                    </div>
                    <div className="p-4">
                      <div className="flex justify-between items-start mb-2">
                        <h3 className="font-semibold text-foreground line-clamp-1">{video.video_title}</h3>
                        {getStatusBadge(video.status)}
                      </div>
                      {video.description && (
                        <p className="text-sm text-muted-foreground mb-3 line-clamp-2">{video.description}</p>
                      )}
                      {video.status === 'rejected' && video.rejection_reason && (
                        <div className="text-sm text-red-400 bg-red-500/10 p-2 rounded mb-3">
                          <strong>Reason:</strong> {video.rejection_reason}
                        </div>
                      )}
                      <div className="flex justify-between items-center">
                        <span className="text-xs text-muted-foreground">
                          Submitted {new Date(video.created_at).toLocaleDateString()}
                        </span>
                        <div className="flex gap-2">
                          {videoId && (
                            <a 
                              href={`https://www.youtube.com/watch?v=${videoId}`}
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-muted-foreground hover:text-foreground"
                              title="Open on YouTube"
                            >
                              <ExternalLink className="w-4 h-4" />
                            </a>
                          )}
                          <button 
                            onClick={() => deleteVideo(video.id)}
                            className="text-red-400 hover:text-red-300"
                            data-testid="delete-video-btn"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
